import { useState } from 'react';
import { Badge, Note, Panel } from '@wyocspay/ui';
import {
  ENDPOINTS,
  ERROR_CODES,
  ERRORS,
  GROUPS,
  INTRO,
  LANGUAGES,
  type Endpoint,
  type Lang,
} from './api-reference.js';

/**
 * The API reference, in the reader's language.
 *
 * Paths, parameter names and code stay in English throughout. Translating an
 * identifier would produce a document that reads well and cannot be typed.
 */

const METHOD_TONE: Record<string, 'ok' | 'info' | 'warn' | 'fail'> = {
  GET: 'info',
  POST: 'ok',
  PATCH: 'warn',
  DELETE: 'fail',
};

function Code({ children }: { children: string }) {
  return (
    <pre
      style={{
        margin: 0,
        padding: '12px 14px',
        background: 'var(--wc-ink)',
        color: '#e8f0f6',
        fontSize: 12.5,
        lineHeight: 1.55,
        overflowX: 'auto',
        whiteSpace: 'pre',
      }}
    >
      <code>{children}</code>
    </pre>
  );
}

export function ApiDocsView() {
  /** Defaults to the browser's language when it is one we have. */
  const [lang, setLang] = useState<Lang>(() => {
    const guess = (navigator.language ?? 'en').slice(0, 2).toLowerCase();
    return LANGUAGES.some((l) => l.code === guess) ? (guess as Lang) : 'en';
  });
  const [open, setOpen] = useState<string | null>(ENDPOINTS[0]?.id ?? null);

  const intro = INTRO[lang];
  const errors = ERRORS[lang];
  /**
   * Ordered by the GROUPS declaration rather than by the order endpoints happen
   * to appear in the array. New entries get appended to the end of ENDPOINTS,
   * so deriving the order from that would scatter a group across the page the
   * first time somebody adds to one that already exists.
   */
  const groups = Object.keys(GROUPS).filter((g) => ENDPOINTS.some((e) => e.group === g));

  return (
    <div style={{ display: 'grid', gap: 16, maxWidth: 940 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: 12.5, color: 'var(--wc-muted)' }}>Language</span>
        <select
          className="wc-input"
          value={lang}
          onChange={(e) => setLang(e.target.value as Lang)}
          style={{ width: 'auto' }}
        >
          {LANGUAGES.map((l) => (
            <option key={l.code} value={l.code}>
              {l.label}
            </option>
          ))}
        </select>
      </div>

      <Panel title={intro.heading}>
        {intro.body.map((p, i) => (
          <p key={i} style={{ margin: i === 0 ? '0 0 10px' : '0 0 10px', fontSize: 14, lineHeight: 1.6 }}>
            {p}
          </p>
        ))}
        <Code>{`Authorization: Bearer sk_live_...
Content-Type: application/json
Idempotency-Key: <your own unique value>`}</Code>
      </Panel>

      {groups.map((group) => (
        <div key={group}>
          <h2
            style={{
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: '0.09em',
              textTransform: 'uppercase',
              color: 'var(--wc-faint)',
              margin: '8px 0 8px',
            }}
          >
            {GROUPS[group]?.[lang] ?? group}
          </h2>
          <div style={{ display: 'grid', gap: 10 }}>
            {ENDPOINTS.filter((e) => e.group === group).map((e) => (
              <EndpointCard
                key={e.id}
                endpoint={e}
                lang={lang}
                open={open === e.id}
                onToggle={() => setOpen(open === e.id ? null : e.id)}
              />
            ))}
          </div>
        </div>
      ))}

      <Panel title={errors.heading}>
        <table className="wc-table">
          <tbody>
            {errors.rows.map(([code, text]) => (
              <tr key={code}>
                <td className="wc-num" style={{ width: 60, verticalAlign: 'top', fontWeight: 600 }}>
                  {code}
                </td>
                <td style={{ fontSize: 13.5 }}>{text}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
    </div>
  );
}

function EndpointCard({
  endpoint,
  lang,
  open,
  onToggle,
}: {
  endpoint: Endpoint;
  lang: Lang;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <div style={{ border: '1px solid var(--wc-border)', background: 'var(--wc-surface)' }}>
      <button
        type="button"
        onClick={onToggle}
        style={{
          display: 'flex',
          gap: 12,
          alignItems: 'center',
          width: '100%',
          padding: '12px 14px',
          border: 'none',
          background: 'transparent',
          textAlign: 'left',
          cursor: 'pointer',
        }}
      >
        <Badge tone={METHOD_TONE[endpoint.method] ?? 'neutral'} icon={false}>
          {endpoint.method}
        </Badge>
        <code className="wc-num" style={{ fontSize: 13, flex: 'none' }}>
          {endpoint.path}
        </code>
        <span style={{ flex: 1, fontSize: 13.5, color: 'var(--wc-muted)', minWidth: 0 }}>
          {endpoint.title[lang]}
        </span>
        <span style={{ color: 'var(--wc-faint)', fontSize: 12 }}>{open ? '−' : '+'}</span>
      </button>

      {open ? (
        <div style={{ padding: '0 14px 14px', display: 'grid', gap: 12 }}>
          <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6 }}>{endpoint.summary[lang]}</p>

          {endpoint.note ? <Note tone="warn">{endpoint.note[lang]}</Note> : null}

          {endpoint.params && endpoint.params.length > 0 ? (
            <table className="wc-table">
              <tbody>
                {endpoint.params.map((p) => (
                  <tr key={p.name}>
                    <td style={{ verticalAlign: 'top', width: 190 }}>
                      <code className="wc-num" style={{ fontSize: 12.5 }}>
                        {p.name}
                      </code>
                      <div style={{ fontSize: 11.5, color: 'var(--wc-faint)' }}>
                        {p.type}
                        {p.required ? ' · required' : ''}
                      </div>
                    </td>
                    <td style={{ fontSize: 13.5 }}>{p.description[lang]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}

          {endpoint.request ? (
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--wc-faint)', marginBottom: 5 }}>
                Request
              </div>
              <Code>{endpoint.request}</Code>
            </div>
          ) : null}

          <div>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--wc-faint)', marginBottom: 5 }}>
              Response
            </div>
            <Code>{endpoint.response}</Code>
          </div>

          {/*
            The errors this particular endpoint can produce.

            Separate from the table at the foot of the page, which covers the
            statuses every endpoint shares. What a reader actually wants when
            something fails is the shorter list: of all the things that can go
            wrong, which ones can go wrong *here*.
          */}
          {endpoint.errors && endpoint.errors.length > 0 ? (
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--wc-faint)', marginBottom: 5 }}>
                Errors
              </div>
              <table className="wc-table">
                <tbody>
                  {endpoint.errors.map((code) => {
                    const entry = ERROR_CODES[code];
                    if (!entry) return null;
                    return (
                      <tr key={code}>
                        <td style={{ verticalAlign: 'top', width: 190 }}>
                          <code className="wc-num" style={{ fontSize: 12.5 }}>
                            {code}
                          </code>
                          <div style={{ fontSize: 11.5, color: 'var(--wc-faint)' }}>{entry.status}</div>
                        </td>
                        <td style={{ fontSize: 13.5 }}>{entry.text[lang]}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
